import { JsonValue } from "../json";
import {
  TypeRef,
  TypeSystem,
  findType,
  listOf,
  named,
  nonNull,
  pathToString,
  resolvePathType,
  typeToString,
  unwrapNamed,
} from "../types/type-system";
import { Expr } from "./ast";
import { ArgInfo, getFunction } from "./functions";

export interface TypeEnv {
  ts: TypeSystem;
  root: TypeRef;
  vars?: ReadonlyMap<string, TypeRef>;
}

export type TypeResult =
  | { ok: true; type: TypeRef }
  | { ok: false; error: string };

const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T[\d:.]+(Z|[+-]\d{2}:?\d{2})?$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function literalType(value: JsonValue): TypeRef {
  if (value === null) {
    return named("JSON");
  }
  if (typeof value === "string") {
    if (ISO_DATETIME.test(value)) {
      return nonNull("DateTime");
    }
    if (ISO_DATE.test(value)) {
      return nonNull("Date");
    }
    return nonNull("String");
  }
  if (typeof value === "number") {
    return nonNull(Number.isInteger(value) ? "Int" : "Float");
  }
  if (typeof value === "boolean") {
    return nonNull("Boolean");
  }
  if (Array.isArray(value)) {
    return listOf(literalElementType(value), false);
  }
  return nonNull("JSON");
}

/** One type for every item: numbers widen to Float, text (dates too) to String, anything else mixed is JSON. */
function literalElementType(items: JsonValue[]): TypeRef {
  const present = items.filter((item) => item !== null);
  const nullable = present.length < items.length;
  const types = present.map(literalType);
  const [first] = types;
  if (!first) {
    return named("JSON");
  }
  const names = new Set(types.map(typeToString));
  if (names.size === 1) {
    return { ...first, nullable };
  }
  if (present.every((item) => typeof item === "number")) {
    return named("Float", nullable);
  }
  if (
    types.every(
      (t) => t.kind === "named" && (t.name === "Date" || t.name === "DateTime"),
    )
  ) {
    return named("DateTime", nullable);
  }
  if (present.every((item) => typeof item === "string")) {
    return named("String", nullable);
  }
  return named("JSON");
}

/** A resolved type whose named type the type system doesn't have is an error, not a later crash. */
function known(ts: TypeSystem, type: TypeRef, label: string): TypeResult {
  const name = unwrapNamed(type).name;
  return findType(ts, name)
    ? { ok: true, type }
    : { ok: false, error: `${label}: unknown type ${name}` };
}

export function inferType(expr: Expr, env: TypeEnv): TypeResult {
  switch (expr.kind) {
    case "literal":
      return { ok: true, type: literalType(expr.value) };
    case "ref": {
      const resolved = resolvePathType(env.ts, env.root, expr.path);
      return resolved.ok
        ? known(env.ts, resolved.type, pathToString(expr.path))
        : { ok: false, error: `${pathToString(expr.path)}: ${resolved.error}` };
    }
    case "var": {
      const varType = env.vars?.get(expr.name);
      if (!varType) {
        return { ok: false, error: `Unknown variable ${expr.name}` };
      }
      if (expr.path.length === 0) {
        return known(env.ts, varType, expr.name);
      }
      const resolved = resolvePathType(env.ts, varType, expr.path);
      return resolved.ok
        ? known(
            env.ts,
            resolved.type,
            `${expr.name}.${pathToString(expr.path)}`,
          )
        : {
            ok: false,
            error: `${expr.name}.${pathToString(expr.path)}: ${resolved.error}`,
          };
    }
    case "lambda":
      return {
        ok: false,
        error: "A predicate can only be used inside a list function",
      };
    case "call": {
      const fn = getFunction(expr.fn);
      if (!fn) {
        return { ok: false, error: `Unknown function ${expr.fn}` };
      }
      const argInfos: ArgInfo[] = [];
      for (const arg of expr.args) {
        if (arg.kind === "lambda") {
          argInfos.push({ kind: "lambda" });
          continue;
        }
        const t = inferType(arg, env);
        if (!t.ok) {
          return t;
        }
        argInfos.push(
          arg.kind === "literal"
            ? { kind: "value", type: t.type, literal: { value: arg.value } }
            : { kind: "value", type: t.type },
        );
      }
      const checkedLambdas = new Set<number>();
      const result = fn.typeCheck(
        {
          ts: env.ts,
          lambdaType(index, paramType) {
            const lambdaArg = expr.args[index];
            if (!lambdaArg || lambdaArg.kind !== "lambda") {
              return "expected a predicate";
            }
            checkedLambdas.add(index);
            const vars = new Map(env.vars ?? []);
            vars.set(lambdaArg.param, paramType);
            const body = inferType(lambdaArg.body, { ...env, vars });
            return body.ok ? body.type : body.error;
          },
        },
        argInfos,
      );
      if (typeof result === "string") {
        return { ok: false, error: `${fn.label}: ${result}` };
      }
      // Evaluation would throw on a predicate the function reads as a value.
      const unchecked = expr.args.findIndex(
        (a, i) => a.kind === "lambda" && !checkedLambdas.has(i),
      );
      return unchecked === -1
        ? { ok: true, type: result }
        : {
            ok: false,
            error: `${fn.label}: argument ${unchecked + 1} must be a value`,
          };
    }
  }
}
